import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { tournamentsApi, competitionApi } from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  TrophyIcon,
  CalendarIcon,
  UserGroupIcon,
  PlayIcon,

  ClockIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'

const tournamentSchema = z.object({
  name: z.string().min(2, 'Tournament name must be at least 2 characters'),
  description: z.string().optional(),
  competition_id: z.number().min(1, 'Please select a competition'),
  type: z.enum(['single_elimination', 'double_elimination', 'round_robin', 'swiss']),
  format: z.enum(['bracket', 'group_stage', 'hybrid']),
  max_participants: z.number().min(2, 'Must allow at least 2 participants').optional(),
  registration_deadline: z.string().min(1, 'Registration deadline is required'),
  start_date: z.string().min(1, 'Start date is required'),
  end_date: z.string().min(1, 'End date is required'),
  is_active: z.boolean()
})

type TournamentForm = z.infer<typeof tournamentSchema>

interface Tournament {
  id: number
  name: string
  description: string
  competition_id: number
  competition_name: string
  competition_category: string
  type: 'single_elimination' | 'double_elimination' | 'round_robin' | 'swiss'
  format: 'bracket' | 'group_stage' | 'hybrid'
  max_participants?: number
  registration_deadline: string
  start_date: string
  end_date: string
  is_active: boolean
  participant_count: number
  status: string
  created_at: string
}

const tournamentTypeLabels = {
  single_elimination: 'Single Elimination',
  double_elimination: 'Double Elimination',
  round_robin: 'Round Robin',
  swiss: 'Swiss System'
}

const formatLabels = {
  bracket: 'Bracket',
  group_stage: 'Group Stage',
  hybrid: 'Hybrid'
}

const TournamentManagement: React.FC = () => {
  const navigate = useNavigate()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTournament, setEditingTournament] = useState<Tournament | null>(null)
  const [filterType, setFilterType] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')

  const queryClient = useQueryClient()

  const { data: tournaments = [], isLoading, error } = useQuery<any>(
    'admin-tournaments',
    tournamentsApi.getTournaments,
    {
      select: (response) => response
    }
  )

  const { data: competitions = [] } = useQuery(
    'competitions-for-tournaments',
    competitionApi.getCompetitions,
    {
      select: (response) => response.data
    }
  )

  const createMutation = useMutation(tournamentsApi.createTournament, {
    onSuccess: () => {
      queryClient.invalidateQueries('admin-tournaments')
      setIsModalOpen(false)
      reset()
      toast.success('Tournament created successfully')
    },
    onError: (_error: any) => {
      toast.error((error as any)?.response?.data?.error || 'Failed to create tournament')
    }
  })

  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: Partial<Tournament> }) =>
      tournamentsApi.updateTournament(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-tournaments')
        setIsModalOpen(false)
        setEditingTournament(null)
        reset()
        toast.success('Tournament updated successfully')
      },
      onError: (_error: any) => {
        toast.error((error as any)?.response?.data?.error || 'Failed to update tournament')
      }
    }
  )

  const deleteMutation = useMutation(tournamentsApi.deleteTournament, {
    onSuccess: () => {
      queryClient.invalidateQueries('admin-tournaments')
      toast.success('Tournament deleted successfully')
    },
    onError: (_error: any) => {
      toast.error((error as any)?.response?.data?.error || 'Failed to delete tournament')
    }
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    // watch - form watching functionality
  } = useForm<TournamentForm>({
    resolver: zodResolver(tournamentSchema),
    defaultValues: {
      is_active: true,
      type: 'single_elimination',
      format: 'bracket'
    }
  })

  const filteredTournaments = tournaments.filter((tournament: Tournament) => {
    const matchesSearch = tournament.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tournament.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tournament.competition_name?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = filterType === 'all' || tournament.type === filterType
    const matchesStatus = filterStatus === 'all' || tournament.status === filterStatus
    return matchesSearch && matchesType && matchesStatus
  })

  const openEditModal = (tournament: Tournament) => {
    setEditingTournament(tournament)
    setValue('name', tournament.name)
    setValue('description', tournament.description || '')
    setValue('competition_id', tournament.competition_id)
    setValue('type', tournament.type)
    setValue('format', tournament.format)
    setValue('max_participants', tournament.max_participants)
    setValue('registration_deadline', tournament.registration_deadline.split('T')[0])
    setValue('start_date', tournament.start_date.split('T')[0])
    setValue('end_date', tournament.end_date.split('T')[0])
    setValue('is_active', tournament.is_active)
    setIsModalOpen(true)
  }

  const onSubmit = (data: TournamentForm) => {
    const submissionData = {
      ...data,
      registration_deadline: new Date(data.registration_deadline).toISOString(),
      start_date: new Date(data.start_date).toISOString(),
      end_date: new Date(data.end_date).toISOString()
    }

    if (editingTournament) {
      updateMutation.mutate({ id: editingTournament.id, data: submissionData })
    } else {
      createMutation.mutate(submissionData)
    }
  }

  const handleDelete = (tournamentId: number, tournamentName: string) => {
    if (window.confirm(`Are you sure you want to delete "${tournamentName}"? This action cannot be undone.`)) {
      deleteMutation.mutate(tournamentId)
    }
  }

  return (
    <>
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tournament Management</h1>
            <p className="text-gray-600">Create and manage tournaments for competitions</p>
          </div>
          <button
            onClick={() => {
              setEditingTournament(null)
              reset({
                is_active: true,
                type: 'single_elimination',
                format: 'bracket'
              })
              setIsModalOpen(true)
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Tournament
          </button>
        </div>
      </div>




      {error && (
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Unable to load tournaments</h2>
          <p className="text-gray-600">Please try again later.</p>
        </div>
      )}

      {!isLoading && !error && (
        <>
          {/* Filters */}
          <div className="mb-6 bg-white p-4 rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <input
                  type="text"
                  placeholder="Search tournaments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Types</option>
                  <option value="single_elimination">Single Elimination</option>
                  <option value="double_elimination">Double Elimination</option>
                  <option value="round_robin">Round Robin</option>
                  <option value="swiss">Swiss System</option>
                </select>
              </div>
              <div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="text-sm text-gray-600 flex items-center">
                <TrophyIcon className="h-4 w-4 mr-1" />
                {filteredTournaments.length} tournament{filteredTournaments.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Tournaments Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tournament
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Competition
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type & Format
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Participants
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Schedule
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTournaments.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      <TrophyIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-lg font-medium">No tournaments found</p>
                      <p className="text-sm">Create your first tournament to get started.</p>
                    </td>
                  </tr>
                ) : (
                  filteredTournaments.map((tournament: Tournament) => (
                    <tr key={tournament.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{tournament.name}</div>
                          {tournament.description && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {tournament.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{tournament.competition_name}</div>
                        <div className="text-sm text-gray-500 capitalize">
                          {tournament.competition_category?.replace('_', ' ')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {tournamentTypeLabels[tournament.type]}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatLabels[tournament.format]}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <UserGroupIcon className="h-4 w-4 mr-1" />
                          {tournament.participant_count}
                          {tournament.max_participants && ` / ${tournament.max_participants}`}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          {new Date(tournament.start_date).toLocaleDateString()}
                        </div>
                        <div className="flex items-center mt-1">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          Reg: {new Date(tournament.registration_deadline).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          tournament.is_active
                            ? tournament.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : tournament.status === 'active'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {tournament.is_active ? tournament.status : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => openEditModal(tournament)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit Tournament"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/admin/tournaments/${tournament.id}/bracket`)}
                            className="text-green-600 hover:text-green-900"
                            title="View Bracket"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {/* TODO: Navigate to matches */}}
                            className="text-purple-600 hover:text-purple-900"
                            title="Manage Matches"
                          >
                            <PlayIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(tournament.id, tournament.name)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Tournament"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Tournament Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingTournament ? 'Edit Tournament' : 'Create New Tournament'}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Tournament Name */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tournament Name *
                    </label>
                    <input
                      type="text"
                      {...register('name')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter tournament name"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      {...register('description')}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter tournament description"
                    />
                  </div>

                  {/* Competition */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Competition *
                    </label>
                    <select
                      {...register('competition_id', { valueAsNumber: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a competition</option>
                      {competitions.map((competition: any) => (
                        <option key={competition.id} value={competition.id}>
                          {competition.name} ({competition.category?.replace('_', ' ')})
                        </option>
                      ))}
                    </select>
                    {errors.competition_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.competition_id.message}</p>
                    )}
                  </div>

                  {/* Tournament Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tournament Type *
                    </label>
                    <select
                      {...register('type')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="single_elimination">Single Elimination</option>
                      <option value="double_elimination">Double Elimination</option>
                      <option value="round_robin">Round Robin</option>
                      <option value="swiss">Swiss System</option>
                    </select>
                  </div>

                  {/* Format */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Format *
                    </label>
                    <select
                      {...register('format')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="bracket">Bracket</option>
                      <option value="group_stage">Group Stage</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </div>

                  {/* Max Participants */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Participants
                    </label>
                    <input
                      type="number"
                      {...register('max_participants', { valueAsNumber: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Leave empty for unlimited"
                      min="2"
                    />
                    {errors.max_participants && (
                      <p className="mt-1 text-sm text-red-600">{errors.max_participants.message}</p>
                    )}
                  </div>

                  {/* Registration Deadline */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Registration Deadline *
                    </label>
                    <input
                      type="date"
                      {...register('registration_deadline')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.registration_deadline && (
                      <p className="mt-1 text-sm text-red-600">{errors.registration_deadline.message}</p>
                    )}
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      {...register('start_date')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.start_date && (
                      <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
                    )}
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date *
                    </label>
                    <input
                      type="date"
                      {...register('end_date')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.end_date && (
                      <p className="mt-1 text-sm text-red-600">{errors.end_date.message}</p>
                    )}
                  </div>

                  {/* Active Status */}
                  <div className="md:col-span-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        {...register('is_active')}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Tournament is active
                      </label>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {createMutation.isLoading || updateMutation.isLoading ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      editingTournament ? 'Update Tournament' : 'Create Tournament'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default TournamentManagement